import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/Header";
import Landing from "@/pages/Landing";
import Browse from "@/pages/Browse";
import ItemDetail from "@/pages/ItemDetail";
import Sell from "@/pages/Sell";
import Messages from "@/pages/Messages";
import Profile from "@/pages/Profile";
import Favorites from "@/pages/Favorites";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import { MentionsLegales, CGV } from "@/pages/Legal";

function Layout({ children, hideHeader = false }) {
  return (
    <div className="App min-h-screen bg-background text-foreground flex flex-col">
      {!hideHeader && <Header />}
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer className="mt-16 bg-secondary/40 border-t border-border" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
        <div>
          <Link to="/" className="flex items-baseline">
            <span className="font-display text-xl font-semibold text-primary">VintedStyle</span>
            <span className="font-display text-xl font-medium">.be</span>
          </Link>
          <p className="mt-3 text-sm text-muted-foreground max-w-xs">
            {t("footer.tagline")}
          </p>
        </div>

        <div>
          <div className="overline text-muted-foreground mb-3">{t("footer.platform")}</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/browse" className="hover:text-primary">{t("nav.browse")}</Link></li>
            <li><Link to="/sell" className="hover:text-primary">{t("nav.sell")}</Link></li>
            <li><a href="/#beta" className="hover:text-primary">{t("footer.beta_program")}</a></li>
          </ul>
        </div>

        <div>
          <div className="overline text-muted-foreground mb-3">{t("footer.help")}</div>
          <ul className="space-y-2 text-sm">
            <li><a href="#" className="hover:text-primary">{t("footer.how_it_works")}</a></li>
            <li><a href="#" className="hover:text-primary">{t("footer.shipping")}</a></li>
            <li>
              <Link to="/legal/cgv" data-testid="footer-cgv" className="hover:text-primary">
                {t("footer.terms")}
              </Link>
            </li>
            <li>
              <Link to="/legal/mentions" data-testid="footer-mentions" className="hover:text-primary">
                {t("legal.mentions.title")}
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <div className="overline text-muted-foreground mb-3">{t("footer.contact")}</div>
          <address className="not-italic text-sm text-muted-foreground leading-relaxed" data-testid="footer-address">
            Rue d'Écosse 34<br />
            1060 Saint-Gilles<br />
            Bruxelles · Belgique 🇧🇪
          </address>
          <a
            href="mailto:support.clients@vintedstyle.be"
            data-testid="footer-email"
            className="mt-3 inline-block text-sm text-primary hover:underline underline-offset-4"
          >
            support.clients@vintedstyle.be
          </a>
        </div>
      </div>
    </footer>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<Layout><Landing /></Layout>} />
          <Route path="/browse" element={<Layout><Browse /></Layout>} />
          <Route path="/item/:id" element={<Layout><ItemDetail /></Layout>} />
          <Route path="/sell" element={<Layout><Sell /></Layout>} />
          <Route path="/messages" element={<Layout><Messages /></Layout>} />
          <Route path="/profile" element={<Layout><Profile /></Layout>} />
          <Route path="/favorites" element={<Layout><Favorites /></Layout>} />
          <Route path="/login" element={<Layout hideHeader><Login /></Layout>} />
          <Route path="/register" element={<Layout hideHeader><Register /></Layout>} />
          <Route path="/payment/success" element={<Layout><PaymentSuccess /></Layout>} />
          <Route path="/payment/cancel" element={<Layout><PaymentCancel /></Layout>} />
          <Route path="/legal/mentions" element={<Layout><MentionsLegales /></Layout>} />
          <Route path="/legal/cgv" element={<Layout><CGV /></Layout>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
